import { WorkExperienceCompany, WorkExperienceRole, WorkExperienceBullet } from '@applicator/shared'

import { mapCompaniesWithToggledBullet, removeBulletFromRole, mapCompaniesWithUpdatedBullet } from './bulletHelpers'
import { initializeDatePicker } from './dateHandlers'
import { initializeRoleEditing, initializeRoleLocationEditing, finalizeRoleLocationEditing, findBulletById } from './editing'
import { updateSingleRoleField } from './helpers'
import { normalizeDateValue, isPresentDate } from './helpers'
import { updateRoleInCompanyForBulletReorder } from './reorderHelpers'
import { updateRoleInCompanyForToggle, updateAllRolesInCompany, mapCompaniesWithUpdatedEmploymentType, mapCompaniesWithDeletedRole, mapCompaniesWithDeletedBullet, mapCompaniesWithUpdatedRole } from './roleHelpers'

// Handle toggle position
export function handleTogglePosition(
  companyId: string,
  roleId: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void
) {
  const updated = companies.map((company) =>
    company.id === companyId ? updateRoleInCompanyForToggle(company, roleId, 'positionIncluded') : company
  )
  onChange(updated)
}

// Handle toggle location
export function handleToggleLocation(
  companyId: string,
  roleId: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void
) {
  const updated = companies.map((company) =>
    company.id === companyId ? updateRoleInCompanyForToggle(company, roleId, 'locationIncluded') : company
  )
  onChange(updated)
}

// Handle toggle bullet
export function handleToggleBullet(
  companyId: string,
  roleId: string,
  bulletId: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void
) {
  const updated = mapCompaniesWithToggledBullet(companies, companyId, roleId, bulletId)
  onChange(updated)
}

// Handle update employment type
export function handleUpdateEmploymentType(
  companyId: string,
  roleId: string,
  employmentType: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void,
  setOpenTypeDropdown: (state: { companyId: string; roleId: string } | null) => void
) {
  const updated = mapCompaniesWithUpdatedEmploymentType(companies, companyId, roleId, employmentType)
  onChange(updated)
  setOpenTypeDropdown(null)
}

// Handle open type dropdown
export function handleOpenTypeDropdown(
  companyId: string,
  roleId: string,
  event: React.MouseEvent,
  typeDropdownRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>,
  setOpenTypeDropdown: (state: { companyId: string; roleId: string } | null) => void
) {
  event.stopPropagation()
  const triggerElement = event.currentTarget as HTMLElement
  const triggerKey = `${companyId}-${roleId}-type`
  typeDropdownRefs.current[triggerKey] = triggerElement
  setOpenTypeDropdown({ companyId, roleId })
}

// Handle close type dropdown
export function handleCloseTypeDropdown(
  setOpenTypeDropdown: (state: { companyId: string; roleId: string } | null) => void
) {
  setOpenTypeDropdown(null)
}

// Handle delete role
export function handleDeleteRole(
  companyId: string,
  roleId: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void
) {
  const updated = mapCompaniesWithDeletedRole(companies, companyId, roleId)
  onChange(updated)
}

// Handle delete bullet
export function handleDeleteBullet(
  companyId: string,
  roleId: string,
  bulletId: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void
) {
  const updated = mapCompaniesWithDeletedBullet(companies, companyId, roleId, bulletId, removeBulletFromRole)
  onChange(updated)
}

// Handle reorder bullets
export function handleReorderBullets(
  companyId: string,
  roleId: string,
  draggedBulletId: string,
  targetIndex: number,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void
) {
  const updated = companies.map((company) =>
    company.id === companyId ? updateRoleInCompanyForBulletReorder(company, roleId, draggedBulletId, targetIndex) : company
  )
  onChange(updated)
}

// Handle update role
export function handleUpdateRole(
  companyId: string,
  roleId: string,
  field: 'position' | 'location' | 'employmentType' | 'startDate' | 'endDate',
  value: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void
) {
  const updated = mapCompaniesWithUpdatedRole(companies, companyId, roleId, field, value, updateAllRolesInCompany, updateSingleRoleField)
  onChange(updated)
}

// Handle update bullet
export function handleUpdateBullet(
  companyId: string,
  roleId: string,
  bulletId: string,
  text: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void
) {
  const updated = mapCompaniesWithUpdatedBullet(companies, companyId, roleId, bulletId, text)
  onChange(updated)
}

// Handle start edit role
export function handleStartEditRole(
  role: WorkExperienceRole,
  setEditingRoleId: (id: string | null) => void,
  setEditingRolePosition: (value: string) => void
) {
  initializeRoleEditing(role, setEditingRoleId, setEditingRolePosition)
}

// Handle save role
export function handleSaveRole(
  companyId: string,
  roleId: string,
  editingRolePosition: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void,
  setEditingRoleId: (id: string | null) => void
) {
  handleUpdateRole(companyId, roleId, 'position', editingRolePosition, companies, onChange)
  setEditingRoleId(null)
}

// Handle start edit role location
export function handleStartEditRoleLocation(
  role: WorkExperienceRole,
  setEditingRoleLocationId: (id: string | null) => void,
  setEditingRoleLocation: (value: string) => void
) {
  initializeRoleLocationEditing(role, setEditingRoleLocationId, setEditingRoleLocation)
}

// Handle save role location
export function handleSaveRoleLocation(
  companyId: string,
  roleId: string,
  editingRoleLocation: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void,
  setEditingRoleLocationId: (id: string | null) => void
) {
  finalizeRoleLocationEditing(companyId, roleId, editingRoleLocation, (companyId, roleId, field, value) => {
    handleUpdateRole(companyId, roleId, field, value, companies, onChange)
  }, setEditingRoleLocationId)
}

// Handle start edit role start date
export function handleStartEditRoleStartDate(
  role: WorkExperienceRole,
  companyId: string,
  event: React.MouseEvent,
  dateFieldRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>,
  setDatePickerState: (state: {
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null) => void
) {
  initializeDatePicker(role, companyId, 'startDate', event, dateFieldRefs, setDatePickerState)
}

// Handle start edit role end date
export function handleStartEditRoleEndDate(
  role: WorkExperienceRole,
  companyId: string,
  event: React.MouseEvent,
  dateFieldRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>,
  setDatePickerState: (state: {
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null) => void
) {
  initializeDatePicker(role, companyId, 'endDate', event, dateFieldRefs, setDatePickerState)
}

// Helper function to check if date picker state exists
const hasDatePickerState = (state: {
  companyId: string
  roleId: string
  field: 'startDate' | 'endDate'
  position: { top: number; left: number; width: number }
  triggerKey: string
} | null): state is {
  companyId: string
  roleId: string
  field: 'startDate' | 'endDate'
  position: { top: number; left: number; width: number }
  triggerKey: string
} => {
  return !!state
}

// Helper function to update role with date value
const updateRoleWithDateValue = (
  datePickerState: {
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
  },
  dateValue: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void
) => {
  handleUpdateRole(datePickerState.companyId, datePickerState.roleId, datePickerState.field, dateValue, companies, onChange)
}

// Helper function to close date picker if needed
const closeDatePickerIfNeeded = (
  date: string,
  setDatePickerState: (state: {
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null) => void
) => {
  if (isPresentDate(date)) return
  setDatePickerState(null)
}

// Handle date change
export function handleDateChange(
  date: string,
  datePickerState: {
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void,
  setDatePickerState: (state: {
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null) => void
) {
  if (!hasDatePickerState(datePickerState)) return
  const dateValue = normalizeDateValue(date)
  updateRoleWithDateValue(datePickerState, dateValue, companies, onChange)
  closeDatePickerIfNeeded(date, setDatePickerState)
}

// Handle date picker close
export function handleDatePickerClose(
  setDatePickerState: (state: {
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null) => void
) {
  setDatePickerState(null)
}

// Handle start edit bullet
export function handleStartEditBullet(
  bullet: WorkExperienceBullet,
  setEditingBulletId: (id: string | null) => void,
  setEditingBulletText: (text: string) => void
) {
  setEditingBulletId(bullet.id)
  setEditingBulletText(bullet.text)
}

// Handle cancel edit bullet
export function handleCancelEditBullet(
  editingBulletId: string | null,
  companies: WorkExperienceCompany[],
  setEditingBulletText: (text: string) => void,
  setEditingBulletId: (id: string | null) => void
) {
  const bullet = findBulletById(editingBulletId, companies)
  if (bullet) {
    setEditingBulletText(bullet.text)
  }
  setEditingBulletId(null)
}

// Handle save bullet
export function handleSaveBullet(
  companyId: string,
  roleId: string,
  bulletId: string,
  editingBulletText: string,
  companies: WorkExperienceCompany[],
  onChange: (data: WorkExperienceCompany[]) => void,
  setEditingBulletId: (id: string | null) => void
) {
  handleUpdateBullet(companyId, roleId, bulletId, editingBulletText, companies, onChange)
  setEditingBulletId(null)
}
